import React from "react";
import { Box, Typography, Button, Chip, alpha, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import StyledTextField from "../../../components/Fields/Textfield";
import { displayFileName } from "../../../components/ui/icons";
import type { DocumentSlot, FormProps } from "../types";

interface DocumentContradictionFormProps extends FormProps {
  contradictionName: string;
  onContradictionNameChange: (value: string) => void;
  document1: DocumentSlot;
  document2: DocumentSlot;
  renderDocumentSlot: (
    document: DocumentSlot,
    slotNumber: 1 | 2,
    label: string
  ) => React.ReactNode;
}

const DocumentContradictionForm: React.FC<DocumentContradictionFormProps> = ({
  contradictionName,
  onContradictionNameChange,
  document1,
  document2,
  renderDocumentSlot,
  isProcessing,
  isSmallScreen,
  onOpenBottomDrawer,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Same navy-panel rules as RequestSpecsForm: light ink on the dark panel, no
  // mid-teal text (UI/UX audit 2.4.1).
  const onNavy = !isSmallScreen;
  const bodyColor = onNavy
    ? alpha(theme.palette.common.white, 0.85)
    : theme.palette.text.secondary;
  const successColor = onNavy
    ? theme.palette.brand.cyanHead
    : theme.palette.status.successFg;
  const chipSx = {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    fontSize: "11px",
    color: onNavy ? theme.palette.brand.cyanHead : theme.palette.text.primary,
    borderColor: onNavy
      ? theme.palette.brand.cyanHead
      : theme.palette.primary.main,
  } as const;

  const bothDocumentsChosen = Boolean(document1 && document2);

  return (
    <>
      <StyledTextField
        placeholder={t("functionalities.form.contradictionNamePlaceholder")}
        value={contradictionName}
        required={true}
        label={t("functionalities.form.contradictionNameLabel")}
        onChange={(e) => onContradictionNameChange(e.target.value)}
        disabled={isProcessing}
      />

      {isSmallScreen && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Typography variant="h5">{t("common.upload")}</Typography>
          <Button
            size="large"
            variant="outlined"
            color="primary"
            onClick={onOpenBottomDrawer}
            endIcon={
              <Icon
                icon="hugeicons:arrow-up-right-01"
                width={24}
                height={24}
                color={theme.palette.primary.main}
              />
            }
          >
            {t("functionalities.form.availableDocuments")}
          </Button>
        </Box>
      )}

      <Box >
        <Typography
          variant="h6"
          sx={{
            color: isSmallScreen
              ? theme.palette.text.primary
              : theme.palette.common.white,
            fontWeight: 600,
            fontSize: "18px",
            mb: 0.5,
          }}
        >
          {t("functionalities.form.selectDocuments")}
        </Typography>
        <Typography variant="body2" color={bodyColor}>
          {t("functionalities.form.analyzeContradictionsHint")}
        </Typography>
      </Box>

      {renderDocumentSlot(document1, 1, t("aiInsight.referenceDocument"))}
      {renderDocumentSlot(document2, 2, t("aiInsight.targetDocument"))}

      {/* Selection Status */}
      {(document1 || document2) && (
        <Box
          mt={2}
          p={2}
          bgcolor={alpha(theme.palette.success.main, 0.1)}
          borderRadius={2}
          border={`1px solid ${alpha(theme.palette.success.main, 0.2)}`}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Icon
              icon="mingcute:check-circle-fill"
              style={{ fontSize: 16, color: successColor }}
            />
            <Typography
              variant="body2"
              color={successColor}
              sx={{ fontWeight: 500 }}
            >
              {t("functionalities.form.documentsSelected")}
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            {document1 && (
              <Chip
                label={`${t("functionalities.form.referenceLabel")}: ${displayFileName(
                  "tittle" in document1 ? document1.tittle : document1.name
                )}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={chipSx}
              />
            )}
            {document2 && (
              <Chip
                label={`${t("functionalities.form.targetLabel")}: ${displayFileName(
                  "tittle" in document2 ? document2.tittle : document2.name
                )}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={chipSx}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Instructions — hidden once both documents are chosen, so the panel does
          not confirm and instruct at the same time (UI/UX audit 2.4.2). */}
      {!bothDocumentsChosen && (
        <Box
          p={2}
          bgcolor={alpha(theme.palette.info.main, 0.1)}
          borderRadius={2}
          border={`1px solid ${alpha(theme.palette.info.main, 0.2)}`}
        >
          <Typography
            variant="caption"
            color={onNavy ? theme.palette.common.white : theme.palette.info.dark}
            sx={{
              display: "block",
              fontSize: "12px",
              lineHeight: 1.4,
            }}
          >
            <Icon
              icon="mingcute:information-line"
              style={{
                fontSize: 14,
                marginRight: 4,
                verticalAlign: "text-bottom",
              }}
            />
            {t("functionalities.form.contradictionInstruction")}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default DocumentContradictionForm;
